import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ExportProgress = ({ 
  userProgress = {
    name: "John Doe",
    email: "john.doe@example.com",
    overallProgress: 67,
    completedChapters: 2,
    totalChapters: 3,
    averageScore: 85,
    totalTimeSpent: 1260, // minutes
    startDate: "2024-01-10",
    lastActivity: "2024-01-15"
  }
}) => {
  const [exportFormat, setExportFormat] = useState('pdf');
  const [isExporting, setIsExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);

  const exportFormats = [
    {
      id: 'pdf',
      name: 'PDF Report',
      description: 'Comprehensive progress report in PDF format',
      icon: 'FileText',
      color: 'error'
    },
    {
      id: 'json',
      name: 'JSON Data',
      description: 'Raw progress data in JSON format',
      icon: 'Code',
      color: 'secondary'
    },
    {
      id: 'csv',
      name: 'CSV Export',
      description: 'Spreadsheet-compatible CSV file',
      icon: 'Table',
      color: 'success'
    }
  ];

  const handleExport = async () => {
    setIsExporting(true);
    
    // Simulate export process
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Mock export data based on format
    const exportData = {
      pdf: generatePDFData(),
      json: generateJSONData(),
      csv: generateCSVData()
    };

    // In a real app, this would trigger actual file download
    console.log(`Exporting ${exportFormat} data:`, exportData[exportFormat]);
    
    setIsExporting(false);
    setExportSuccess(true);
    
    // Reset success message after 3 seconds
    setTimeout(() => setExportSuccess(false), 3000);
  };

  const generatePDFData = () => {
    return {
      format: 'pdf',
      title: 'AI Learning Hub - Progress Report',
      user: userProgress,
      generatedAt: new Date().toISOString(),
      sections: [
        'Overall Progress Summary',
        'Chapter-wise Performance',
        'Quiz Results Analysis',
        'Time Spent Breakdown',
        'Achievement Summary'
      ]
    };
  };

  const generateJSONData = () => {
    return {
      format: 'json',
      user: userProgress,
      chapters: [
        {
          id: 1,
          title: "Introduction to AI",
          progress: 100,
          quizScore: 75,
          timeSpent: 45,
          completed: true
        },
        {
          id: 2,
          title: "Machine Learning Basics",
          progress: 100,
          quizScore: 85,
          timeSpent: 52,
          completed: true
        },
        {
          id: 3,
          title: "Neural Networks",
          progress: 0,
          quizScore: 0,
          timeSpent: 0,
          completed: false
        }
      ],
      exportedAt: new Date().toISOString()
    };
  };

  const generateCSVData = () => {
    return {
      format: 'csv',
      headers: ['Chapter', 'Progress', 'Quiz Score', 'Time Spent', 'Status'],
      data: [
        ['Introduction to AI', '100%', '75%', '45 min', 'Completed'],
        ['Machine Learning Basics', '100%', '85%', '52 min', 'Completed'],
        ['Neural Networks', '0%', 'N/A', '0 min', 'Not Started']
      ],
      exportedAt: new Date().toISOString()
    };
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric',
      month: 'long', 
      day: 'numeric'
    });
  };

  return (
    <div className="bg-surface rounded-xl border border-border p-6 card-shadow">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-heading font-semibold text-text-primary">
            Export Progress
          </h3>
          <p className="text-text-secondary mt-1">
            Share your learning achievements with educators or employers
          </p>
        </div>
        <div className="flex items-center space-x-2 bg-primary-50 px-3 py-2 rounded-lg">
          <Icon name="Download" size={16} color="var(--color-primary)" strokeWidth={2} />
          <span className="text-sm font-medium text-primary">Export Ready</span>
        </div>
      </div>

      {/* Progress Summary */}
      <div className="bg-surface-secondary rounded-lg p-4 mb-6">
        <h4 className="text-lg font-heading font-semibold text-text-primary mb-3">
          Progress Summary
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-heading font-bold text-primary mb-1">
              {userProgress.overallProgress}%
            </div>
            <div className="text-sm text-text-secondary">Overall Progress</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-heading font-bold text-success mb-1">
              {userProgress.completedChapters}/{userProgress.totalChapters}
            </div>
            <div className="text-sm text-text-secondary">Chapters Complete</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-heading font-bold text-secondary mb-1">
              {userProgress.averageScore}%
            </div>
            <div className="text-sm text-text-secondary">Average Score</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-heading font-bold text-accent mb-1">
              {formatTime(userProgress.totalTimeSpent)}
            </div>
            <div className="text-sm text-text-secondary">Time Invested</div>
          </div>
        </div>
      </div>

      {/* Export Format Selection */}
      <div className="mb-6">
        <h4 className="text-lg font-heading font-semibold text-text-primary mb-4">
          Choose Export Format
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {exportFormats.map((format) => (
            <div
              key={format.id}
              className={`
                relative border rounded-lg p-4 cursor-pointer transition-all duration-200
                ${exportFormat === format.id
                  ? 'border-primary bg-primary-50 ring-2 ring-primary-100' :'border-border hover:border-border-focus hover:bg-surface-secondary'}
              `}
              onClick={() => setExportFormat(format.id)}
            >
              <div className="flex items-start space-x-3">
                <div className={`
                  flex items-center justify-center w-10 h-10 rounded-lg
                  ${exportFormat === format.id ? 'bg-primary-100' : `bg-${format.color}-50`}
                `}>
                  <Icon 
                    name={format.icon} 
                    size={18} 
                    color={exportFormat === format.id ? 'var(--color-primary)' : `var(--color-${format.color})`}
                    strokeWidth={2}
                  />
                </div>
                <div className="flex-1">
                  <h5 className="font-heading font-semibold text-text-primary mb-1">
                    {format.name}
                  </h5>
                  <p className="text-sm text-text-secondary">
                    {format.description}
                  </p>
                </div>
              </div>
              
              {exportFormat === format.id && (
                <div className="absolute top-2 right-2">
                  <Icon name="CheckCircle2" size={16} color="var(--color-primary)" strokeWidth={2} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Export Details */}
      <div className="bg-surface-secondary rounded-lg p-4 mb-6">
        <h5 className="font-heading font-semibold text-text-primary mb-3">
          Export Details
        </h5>
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-text-secondary">Student Name:</span>
            <span className="font-medium text-text-primary">{userProgress.name}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-text-secondary">Email:</span>
            <span className="font-medium text-text-primary">{userProgress.email}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-text-secondary">Course Started:</span>
            <span className="font-medium text-text-primary">{formatDate(userProgress.startDate)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-text-secondary">Last Activity:</span>
            <span className="font-medium text-text-primary">{formatDate(userProgress.lastActivity)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-text-secondary">Export Date:</span>
            <span className="font-medium text-text-primary">{formatDate(new Date().toISOString())}</span>
          </div>
        </div>
      </div>

      {/* Export Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          variant="primary"
          size="lg"
          fullWidth
          iconName="Download"
          iconPosition="left"
          onClick={handleExport}
          disabled={isExporting}
          loading={isExporting}
        >
          {isExporting ? 'Generating Export...' : `Export as ${exportFormats.find(f => f.id === exportFormat)?.name}`}
        </Button>
        
        <Button
          variant="outline"
          size="lg"
          fullWidth
          iconName="Share"
          iconPosition="left"
          disabled={isExporting}
        >
          Share Progress
        </Button>
      </div>

      {/* Success Message */}
      {exportSuccess && (
        <div className="mt-4 flex items-center space-x-2 bg-success-50 border border-success-200 rounded-lg p-3">
          <Icon name="CheckCircle2" size={16} color="var(--color-success)" strokeWidth={2} />
          <span className="text-sm font-medium text-success-700">
            Progress exported successfully! Check your downloads folder.
          </span>
        </div>
      )}
    </div>
  );
};

export default ExportProgress;